import type { ComponentPropsWithoutRef } from "react";
import { forwardRef, useContext, useEffect, useId, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import clsx from "clsx";
import { FormControlContext } from "~/ui/infra/FormControl";

type InputProps = ComponentPropsWithoutRef<"input"> & {
  error?: boolean;
};

export const InputFilled = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const id = useId();

  const { className } = props;

  const { controlId, controlError } = useContext(FormControlContext);

  return (
    <input
      id={controlId || id}
      {...props}
      ref={ref}
      className={twMerge(
        clsx(
          // min-h aligns compact rows with datetime-local / buttons; border matches InputOutlined (medium, not InputSearch).
          "box-border w-full min-h-11 rounded border bg-black/25 px-2 py-1.5 text-sm text-white autofill:shadow-[inset_0_0_0_1000px_rgb(7,22,41)] autofill:text-fill-white read-only:border-transparent read-only:text-secondary-50 focus:border-primary focus:outline-none focus:ring-0 read-only:focus:border-transparent disabled:border-transparent disabled:text-secondary-50",
          controlError ? "border-danger" : "border-medium"
        ),
        className
      )}
    />
  );
});

export const InputOutlined = forwardRef<HTMLInputElement, InputProps>(({ className, ...props }, ref) => {
  const id = useId();
  const { controlId, controlError } = useContext(FormControlContext);
  return (
    <input
      id={controlId || id}
      ref={ref}
      {...props}
      className={twMerge(
        "w-full rounded border bg-black/25 px-4 pb-3 pt-2.5 text-base text-white placeholder:text-medium autofill:shadow-[inset_0_0_0_1000px_rgb(7,22,41)] autofill:text-fill-white read-only:border-transparent read-only:text-secondary-50 focus:border-primary focus:ring-0 read-only:focus:border-transparent disabled:border-transparent disabled:text-secondary-50",
        controlError ? "border-danger" : "border-medium",
        className
      )}
    />
  );
});
export const InputUnderlined = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
  const id = useId();
  const { controlId, controlError } = useContext(FormControlContext);
  const { className } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (inputRef && inputRef.current) {
      inputRef.current.focus(); // Focus the input when the component mounts
    }
  }, [inputRef]);
  return (
    <input
      id={controlId || id}
      ref={inputRef}
      {...props}
      className={clsx(
        `text-white ${className} w-full appearance-none border-0 border-b-2 bg-transparent px-4 pb-3 pt-2.5 text-base placeholder:text-medium outline-none autofill:shadow-[inset_0_0_0_1000px_rgb(7,22,41)] autofill:text-fill-white read-only:border-transparent read-only:text-secondary-50 focus:border-primary focus:outline-none focus:ring-0 read-only:focus:border-transparent disabled:border-transparent disabled:text-secondary-50`,
        controlError ? "border-b-danger" : "border-b-medium"
      )}
    />
  );
});

export const InputPasswordOutlined = forwardRef<HTMLInputElement, InputProps>(({ className, ...props }, ref) => {
  const id = useId();
  const { controlId, controlError } = useContext(FormControlContext);
  const [isShowed, setIsShowed] = useState(false);

  const handleClick = () => {
    setIsShowed((prev) => !prev);
  };

  return (
    <div className="relative">
      <input
        id={controlId || id}
        ref={ref}
        type={isShowed ? "text" : "password"}
        {...props}
        className={twMerge(
          "w-full rounded border bg-black/25 px-4 pb-3 pt-2.5 text-base text-white placeholder:text-medium autofill:shadow-[inset_0_0_0_1000px_rgb(7,22,41)] autofill:text-fill-white read-only:border-transparent read-only:text-secondary-50 focus:border-primary focus:ring-0 read-only:focus:border-transparent disabled:border-medium disabled:text-secondary-50",
          controlError ? "border-danger" : "border-medium",
          className
        )}
      />
      <button className="absolute right-4 top-3 text-xl" onClick={handleClick} type="button">
        <i className={clsx("text-secondary-50", isShowed ? "icon-eye-open" : "icon-eye-closed")}></i>
      </button>
    </div>
  );
});

export const InputSearch = forwardRef<HTMLInputElement, InputProps & { onIconClick?: () => void; onClear?: () => void }>(
  ({ onIconClick, onClear, ...props }, ref) => {
    const showClear = !!onClear && !!props.value;
    return (
      <div className="relative">
        {onIconClick ? (
          <button type="button" onClick={onIconClick} className="absolute left-2 top-2.5 p-0 leading-none">
            <i className="icon-search text-xl leading-none text-primary"></i>
          </button>
        ) : (
          <i className="icon-search absolute left-2 top-2.5 text-xl leading-none text-primary"></i>
        )}
        <input
          ref={ref}
          {...props}
          className={`w-full rounded border border-secondary-75 bg-black/25 p-2 pl-9 text-sm text-white placeholder:text-medium autofill:shadow-[inset_0_0_0_1000px_rgb(7,22,41)] autofill:text-fill-white read-only:border-transparent read-only:text-secondary-50 focus:border-primary focus:ring-0 read-only:focus:border-transparent disabled:border-medium disabled:text-secondary-50 ${showClear ? "pr-8" : ""}`}
        />
        {showClear && (
          <button
            type="button"
            onClick={onClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 leading-none text-secondary-50 hover:text-white"
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <line x1="1" y1="1" x2="9" y2="9" />
              <line x1="9" y1="1" x2="1" y2="9" />
            </svg>
          </button>
        )}
      </div>
    );
  }
);
